import React, { Component } from "react";
import propTypes from "prop-types";
import { observer, inject } from "mobx-react";
import withRouter from "../../components/withRouter";
import t from "../../service/translate.service";
import LoadingPageOverlay from "../../components/LoadingPageOverlay";
import { Alert, Input, Table, Button, Modal, Spin, message } from "antd";
import { SearchOutlined, EyeOutlined, DownloadOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import {
  extractError,
  extractErrorFromSap,
} from "../../service/response.service";
import { appFeatureEnabled } from "../../service/appFeatures.service";
import NoContent from "../../components/NoContent";
import { ContractsImage } from "../../components/icons";
import { api } from "../../service/api.service";
import { saveAs } from "file-saver";

class CondoInvoicesList extends Component {
  static propTypes = {
    contractStore: propTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      searchTerm: "",
      searchInput: "", // Immediate input value for controlled component
      initialLoading: true,
      contract: null,
      errorMsg: "",
      previewModalVisible: false,
      previewPdfUrl: null,
      previewLoading: false,
      previewError: null,
      downloadingAll: false,
      downloadingBillingUnit: null, 
      selectedBillingUnit: null,
      suppliersExpanded: false, // Collapsed by default
    };
    this.searchDebounceTimer = null;
  }

  async componentDidMount() {
    window.scrollTo(0, 0);

    const contractNumber = this.props.useParams.contractNumber;
    
    try {
      // Get contract and role information
      await this.props.contractStore.initContracts();
      const selectedContract = this.props.contractStore.activeContracts?.find(
        (c) => c.contractNumber === contractNumber
      );
      
      if (!selectedContract) {
        this.setState({ 
          initialLoading: false,
          errorMsg: t.ERROR || "Contract not found"
        });
        return;
      }

      this.setState({ contract: selectedContract });

      const role = this.props.contractStore.role;

      // Check if user has permission to view invoices
      if (!appFeatureEnabled("condoReceiptInspectionFeaturesEnabled", [selectedContract]) ||
          !appFeatureEnabled("pageCondoInvoicesVisibleTo", [{...selectedContract, role}])) {
        this.setState({ 
          initialLoading: false,
          errorMsg: "Access denied"
        });
        return;
      }

      // Fetch invoices
      await this.fetchInvoices();
      this.setState({ initialLoading: false });
    } catch (err) {
      this.setState({
        initialLoading: false,
        errorMsg:
          extractErrorFromSap(err) ||
          t[extractError(err)] ||
          t["ERROR"],
      });
    }
  }

  componentWillUnmount() {
    // Clean up debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  }

  fetchInvoices = async () => {
    const contractNumber = this.props.useParams.contractNumber;
    
    try {
      // Fetch all invoices - uses default size of 100k from store method
      await this.props.contractStore.fetchCondoInvoices(contractNumber);
    } catch (err) {
      this.setState({ 
        errorMsg: extractErrorFromSap(err) || t[extractError(err)] || t["ERROR"]
      });
    }
  };

  handleSearch = (value) => {
    // Update input immediately for responsive typing
    this.setState({ searchInput: value });
    
    // Clear debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    
    // If clearing (empty string), update search immediately
    if (!value || value.trim() === '') {
      this.setState({ searchTerm: '' });
      return;
    }
    
    // Otherwise, debounce the actual search to avoid expensive re-renders
    this.searchDebounceTimer = setTimeout(() => {
      this.setState({ searchTerm: value });
    }, 300); // Wait 300ms after user stops typing
  };

  formatCurrency = (amount, currency = "EUR") => {
    const numAmount = parseFloat(amount || 0);
    return `${numAmount.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency}`;
  };

  formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE");
  };

  filterInvoices = (invoices, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === "") {
      return invoices;
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();

    return invoices.filter((invoice) => {
      // Search across key business fields (excluding addresses to reduce false matches)
      const searchableFields = [
        invoice.propertyName,        // Supplier/Building name
        invoice.supplierNumber,      // Supplier number
        invoice.invoiceId,           // Invoice ID
        invoice.referenceNumber,     // Reference number
        invoice.description,         // Service description
        invoice.headerText,          // Additional invoice text
        invoice.amount?.toString(),  // Amount (useful for "find my 500 euro invoice")
        // Excluded: street, city, postalCode, contractNumber, dates
        // These are displayed but not searchable to avoid false matches
      ];

      return searchableFields.some((field) => {
        if (!field) return false;
        return field.toString().toLowerCase().includes(lowerSearchTerm);
      });
    });
  };

  fetchInvoicePdf = async (invoice) => {
    const contractNumber = this.props.useParams.contractNumber;
    const invoiceId = `${invoice.prepaidNumber}-${invoice.contractNumber}-${invoice.documentId}`;

    console.log('Fetching invoice PDF for invoiceId:', invoiceId);

    const pdfData = await this.props.contractStore.fetchCondoInvoicePdf(contractNumber, invoiceId);
    
    const blob = this.base64ToBlob(pdfData.file, 'application/pdf');
    const fileName = pdfData.fileName || `invoice_${invoice.invoiceId}.pdf`;

    return { blob, fileName };
  };

  base64ToBlob = (base64, contentType = '') => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  };

  handlePreview = async (invoice) => {
    this.setState({ 
      previewModalVisible: true, 
      previewLoading: true,
      previewError: null,
      previewPdfUrl: null 
    });

    try {
      const { blob } = await this.fetchInvoicePdf(invoice);
      const url = URL.createObjectURL(blob);

      this.setState({ 
        previewPdfUrl: url,
        previewLoading: false 
      });
    } catch (error) {
      const errorMessage = extractErrorFromSap(error) || t.CONDO_INVOICE_NOT_FOUND || t.CONDO_INVOICE_PDF_ERROR;
      this.setState({ 
        previewLoading: false,
        previewError: errorMessage
      });
    }
  };

  handleDownload = async (invoice) => {
    try {
      const { blob, fileName } = await this.fetchInvoicePdf(invoice);
      // saveAs(blob, fileName); // production - uses backend provided filename
      saveAs(blob, `invoice_${invoice.invoiceId}.pdf`); // development - uses invoice ID
    } catch (error) {
      console.error('Error downloading PDF:', error);
      const errorMessage = extractErrorFromSap(error) || t.CONDO_INVOICE_NOT_FOUND || "Invoice document could not be found or downloaded";
      message.error(errorMessage);
    }
  };

  handleDownloadAll = async () => {
    this.setState({ downloadingAll: true });
    
    const { condoInvoices } = this.props.contractStore;
    const { searchTerm } = this.state;
    const allInvoices = condoInvoices.invoices || [];
    const filteredInvoices = this.filterInvoices(allInvoices, searchTerm);

    try {
      for (const invoice of filteredInvoices) {
        await this.handleDownload(invoice);
        // Add small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error('Error downloading all PDFs:', error);
    } finally {
      this.setState({ downloadingAll: false });
    }
  };

  handleDownloadByBillingUnit = async (billingUnit) => {
    this.setState({ downloadingBillingUnit: billingUnit.billingUnit });
    
    try {
      for (const invoice of billingUnit.invoices) {
        await this.handleDownload(invoice);
        // Add small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error('Error downloading billing unit PDFs:', error);
    } finally {
      this.setState({ downloadingBillingUnit: null });
    }
  };

  /**
   * Extracts supplier number from invoice
   * Falls back to parsing invoiceId if supplierNumber field is missing
   * InvoiceId format: "companyCode-supplierNumber-referenceNumber"
   */
  getSupplierNumber = (invoice) => {
    let supplierNumber = invoice.supplierNumber;
    
    if (!supplierNumber && invoice.invoiceId) {
      const parts = invoice.invoiceId.split('-');
      if (parts.length >= 2) {
        supplierNumber = parts[1];
      }
    }
    
    // Normalize: trim and remove leading zeros
    return supplierNumber 
      ? String(supplierNumber).trim().replace(/^0+/, '') || '0'
      : 'UNKNOWN';
  };

  /**
   * Creates unique billing unit key using supplier number and property name
   * This ensures proper grouping even when different properties share supplier numbers
   */
  getBillingUnitKey = (invoice) => {
    const normalizedSupplier = this.getSupplierNumber(invoice);
    const propertyName = invoice.propertyName || 'Unknown';
    return `${normalizedSupplier}|${propertyName.trim()}`;
  };

  /**
   * Gets display name for billing unit (shown in UI)
   * Currently shows only property name for cleaner UI
   */
  getBillingUnitDisplayName = (invoice) => {
    return invoice.propertyName?.trim() || 'Unknown';
  };

  /**
   * Groups invoices by billing unit (supplier + property combination)
   * Returns array sorted by most recent invoice date
   */
  groupInvoicesByBillingUnit = (invoices) => {
    const grouped = {};
    
    invoices.forEach(invoice => {
      const billingUnitKey = this.getBillingUnitKey(invoice);
      
      // Initialize group if it doesn't exist
      if (!grouped[billingUnitKey]) {
        grouped[billingUnitKey] = {
          billingUnit: billingUnitKey,
          displayName: this.getBillingUnitDisplayName(invoice),
          supplierNumber: invoice.supplierNumber,
          propertyName: invoice.propertyName,
          invoices: [],
          totalAmount: 0,
          address: `${invoice.street || ''} ${invoice.postalCode || ''} ${invoice.city || ''}`.trim(),
          currency: invoice.currency || 'EUR',
        };
      }
      
      // Add invoice to group and update total
      grouped[billingUnitKey].invoices.push(invoice);
      grouped[billingUnitKey].totalAmount += parseFloat(invoice.amount || 0);
    });

    // Convert to array, add metadata, and sort by most recent date
    return Object.values(grouped)
      .map(group => ({
        ...group,
        count: group.invoices.length,
        mostRecentDate: this.getMostRecentDate(group.invoices),
      }))
      .sort((a, b) => b.mostRecentDate - a.mostRecentDate);
  };

  /**
   * Finds the most recent document date in an array of invoices
   */
  getMostRecentDate = (invoices) => {
    return invoices.reduce((latest, invoice) => {
      const invoiceDate = new Date(invoice.documentDate || 0);
      return invoiceDate > latest ? invoiceDate : latest;
    }, new Date(0));
  };

  handleSelectBillingUnit = (billingUnit) => {
    // Toggle selection - if already selected, clear it; otherwise select it
    this.setState(prevState => ({
      selectedBillingUnit: prevState.selectedBillingUnit === billingUnit ? null : billingUnit,
      // Collapse suppliers section when a billing unit is selected
      suppliersExpanded: prevState.selectedBillingUnit === billingUnit ? prevState.suppliersExpanded : false
    }));
  };

  clearBillingUnitFilter = () => {
    this.setState({ selectedBillingUnit: null });
  };

  toggleSuppliersExpanded = () => {
    this.setState(prevState => ({ 
      suppliersExpanded: !prevState.suppliersExpanded 
    }));
  };

  handleClosePreview = () => {
    if (this.state.previewPdfUrl) {
      URL.revokeObjectURL(this.state.previewPdfUrl);
    }
    this.setState({ 
      previewModalVisible: false, 
      previewPdfUrl: null,
      previewError: null 
    });
  };

  /**
   * Applies filters and sorting to invoice list
   * Returns filtered, sorted invoices and grouped data for supplier cards
   * @returns {Object} { sortedInvoices, groupedData }
   */
  getFilteredAndGroupedInvoices = () => {
    const { condoInvoices } = this.props.contractStore;
    const { searchTerm, selectedBillingUnit } = this.state;
    const allInvoices = condoInvoices.invoices || [];
    
    // Step 1: Apply supplier filter if one is selected
    let filteredInvoices = selectedBillingUnit
      ? allInvoices.filter(invoice => this.getBillingUnitKey(invoice) === selectedBillingUnit)
      : allInvoices;
    
    // Step 2: Apply search filter to the supplier-filtered results
    if (searchTerm) {
      filteredInvoices = this.filterInvoices(filteredInvoices, searchTerm);
    }
    
    // Step 3: Sort by most recent date first
    const sortedInvoices = [...filteredInvoices].sort((a, b) => {
      return new Date(b.documentDate || 0) - new Date(a.documentDate || 0);
    });
    
    // Step 4: Group for supplier cards
    // - If supplier is selected OR no search active: show all suppliers
    // - If search is active and no supplier selected: show suppliers from search results
    const groupedData = selectedBillingUnit || !searchTerm
      ? this.groupInvoicesByBillingUnit(allInvoices)
      : this.groupInvoicesByBillingUnit(sortedInvoices);

    return { sortedInvoices, groupedData };
  };

  render() {
    const { 
      initialLoading, 
      errorMsg, 
      searchTerm,
      searchInput,
      previewModalVisible, 
      previewPdfUrl, 
      previewLoading, 
      previewError,
      downloadingAll,
      downloadingBillingUnit,
      selectedBillingUnit,
      suppliersExpanded
    } = this.state;
    const { condoInvoices, condoInvoicesError, condoInvoicesLoading } =
      this.props.contractStore;

    // Show full-page loader only on initial load
    if (initialLoading) {
      return <LoadingPageOverlay />;
    }

    if (errorMsg || condoInvoicesError) {
      return (
        <div className="contract-list">
          <Alert
            role="alert"
            aria-live="assertive"
            message={errorMsg || condoInvoicesError}
            type="error"
            style={{ marginBottom: "12px", marginTop: "12px" }}
          />
          <Button
            onClick={() => this.props.navigate(-1)}
            style={{ marginTop: "20px" }}
          >
            {t.BACK || "Back"}
          </Button>
        </div>
      );
    }

    const columns = [
      {
        title: t.CONDO_INVOICES_SUPPLIER,
        dataIndex: "propertyName",
        key: "propertyName",
        render: (text, record) => (
          <div>
            <div style={{ fontWeight: "500" }}>{text || "-"}</div>
            {record.street && (
              <div style={{ fontSize: "0.85em", color: "#666" }}>
                {record.street}
              </div>
            )}
            {(record.postalCode || record.city) && (
              <div style={{ fontSize: "0.85em", color: "#666" }}>
                {record.postalCode} {record.city}
              </div>
            )}
          </div>
        ),
      },
      {
        title: t.CONDO_INVOICES_INVOICE_ID,
        dataIndex: "invoiceId",
        key: "invoiceId",
        render: (text, record) => (
          <div>
            <div style={{ fontWeight: "500" }}>{text || "-"}</div>
            {record.referenceNumber && (
              <div style={{ fontSize: "0.85em", color: "#666" }}>
                Ref: {record.referenceNumber}
              </div>
            )}
          </div>
        ),
      },
      {
        title: t.CONDO_INVOICES_DESCRIPTION,
        dataIndex: "description",
        key: "description",
        render: (text, record) => (
          <div>
            <div>{text || "-"}</div>
            {record.headerText && (
              <div style={{ fontSize: "0.85em", color: "#666", marginTop: "4px" }}>
                {record.headerText}
              </div>
            )}
          </div>
        ),
      },
      {
        title: t.CONDO_INVOICES_INVOICE_DATE,
        dataIndex: "documentDate",
        key: "documentDate",
        render: (text) => this.formatDate(text),
        sorter: (a, b) => {
          const dateA = new Date(a.documentDate || 0);
          const dateB = new Date(b.documentDate || 0);
          return dateA - dateB;
        },
      },
      {
        title: t.CONDO_INVOICES_PAYMENT_DATE,
        dataIndex: "postingDate",
        key: "postingDate",
        render: (text) => this.formatDate(text),
        sorter: (a, b) => {
          const dateA = new Date(a.postingDate || 0);
          const dateB = new Date(b.postingDate || 0);
          return dateA - dateB;
        },
      },
      {
        title: t.CONDO_INVOICES_AMOUNT,
        dataIndex: "amount",
        key: "amount",
        render: (text, record) => (
          <div style={{ textAlign: "right", fontWeight: "500" }}>
            {this.formatCurrency(text, record.currency)}
            {record.taxAmount && parseFloat(record.taxAmount) > 0 && (
              <div style={{ fontSize: "0.85em", color: "#666", marginTop: "4px" }}>
                {t.TAX || "Tax"}: {this.formatCurrency(record.taxAmount, record.currency)}
              </div>
            )}
          </div>
        ),
        sorter: (a, b) => {
          const amountA = parseFloat(a.amount || 0);
          const amountB = parseFloat(b.amount || 0);
          return amountA - amountB;
        },
      },
      {
        title: t.CONDO_INVOICE_PREVIEW,
        key: "actions",
        align: "center",
        width: 120,
        render: (text, record) => (
          <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => this.handlePreview(record)}
              title={t.CONDO_INVOICE_PREVIEW}
            />
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => this.handleDownload(record)}
              title={t.CONDO_INVOICE_DOWNLOAD}
            />
          </div>
        ),
      },
    ];

    // Get filtered and grouped data
    const { sortedInvoices, groupedData } = this.getFilteredAndGroupedInvoices();
    const dataSource = sortedInvoices;
    
    // Use client-side pagination for filtered results
    const pagination = {
      defaultPageSize: 10,
      showSizeChanger: true,
      showTotal: (total, range) =>
        `${t.CONDO_INVOICES_SHOWING} ${range[0]}-${range[1]} ${t.CONDO_INVOICES_OF} ${total} ${t.CONDO_INVOICES_ENTRIES}`,
      pageSizeOptions: ["10", "20", "50", "100"],
    };

    return (
      <div className="contract-list">
        <div style={{ marginBottom: "20px" }}>
          <h2>{t.CONDO_INVOICES_TITLE}</h2>
          <p style={{ color: "#666" }}>
            {t.CONTRACT_NUMBER || "Contract Number"}:{" "}
            {this.props.useParams.contractNumber}
          </p>
        </div>

        <div style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
          <Input
            placeholder={t.CONDO_INVOICES_SEARCH}
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={(e) => this.handleSearch(e.target.value)}
            style={{ maxWidth: "400px" }}
            allowClear
          />
          {dataSource.length > 0 && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              disabled
              // onClick={this.handleDownloadAll}
              // loading={downloadingAll}
              // disabled={downloadingBillingUnit !== null}
            >
              {t.CONDO_INVOICE_DOWNLOAD_ALL || "Download All"} ({dataSource.length})
            </Button>
          )}
        </div>

        {/* Show active filters info */}
        {(searchTerm || selectedBillingUnit) && (
          <Alert
            message={
              <div>
                {searchTerm && <span>🔍 Search: "{searchTerm}"</span>}
                {searchTerm && selectedBillingUnit && <span> • </span>}
                {selectedBillingUnit && (
                  <span>
                    📦 Supplier: {groupedData.find(g => g.billingUnit === selectedBillingUnit)?.displayName}
                  </span>
                )}
                <span> → {dataSource.length} {t.CONDO_INVOICES_ENTRIES || "results"}</span>
              </div>
            }
            type="info"
            showIcon
            closable
            onClose={() => {
              this.handleSearch('');
              this.clearBillingUnitFilter();
            }}
            style={{ marginBottom: "16px" }}
          />
        )}

        {/* Supplier Groups (Billing Units) - Collapsible */}
        {groupedData.length > 0 && (
          <div style={{ marginBottom: "32px", padding: "20px", backgroundColor: "#fafafa", borderRadius: "8px", border: "1px solid #e8e8e8" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: suppliersExpanded ? "16px" : "0" }}>
              <div 
                onClick={this.toggleSuppliersExpanded}
                style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "12px",
                  cursor: "pointer",
                  flex: 1,
                  userSelect: "none"
                }}
              >
                <Button 
                  type="primary"
                  ghost
                  size="small"
                  icon={suppliersExpanded ? <UpOutlined /> : <DownOutlined />}
                  style={{
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "32px",
                    height: "32px",
                    transition: "all 0.3s ease"
                  }}
                />
                <h3 style={{ fontSize: "16px", fontWeight: "600", margin: 0, color: "#262626" }}>
                  {t.CONDO_INVOICES_SUPPLIERS || "Suppliers"} 
                  <span style={{ 
                    marginLeft: "8px", 
                    fontSize: "14px", 
                    fontWeight: "400", 
                    color: "#8c8c8c",
                    backgroundColor: "#fff",
                    padding: "2px 8px",
                    borderRadius: "10px",
                    border: "1px solid #e8e8e8"
                  }}>
                    {groupedData.length}
                  </span>
                </h3>
              </div>
              {selectedBillingUnit && suppliersExpanded && (
                <Button 
                  type="link" 
                  size="small" 
                  onClick={this.clearBillingUnitFilter}
                  style={{ padding: "0 8px" }}
                >
                  Clear filter
                </Button>
              )}
            </div>
            {suppliersExpanded && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "12px" }}>
              {groupedData.map((unit, index) => {
                const isSelected = selectedBillingUnit === unit.billingUnit;
                return (
                  <div 
                    key={index}
                    onClick={() => this.handleSelectBillingUnit(unit.billingUnit)}
                    style={{ 
                      padding: "12px", 
                      backgroundColor: isSelected ? "#e6f7ff" : "white", 
                      borderRadius: "6px",
                      border: isSelected ? "2px solid #1890ff" : "1px solid #e0e0e0",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = "#fafafa";
                        e.currentTarget.style.borderColor = "#1890ff";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = "white";
                        e.currentTarget.style.borderColor = "#e0e0e0";
                      }
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ flex: 1, pointerEvents: "none" }}>
                        <div style={{ fontWeight: "600", marginBottom: "4px", color: isSelected ? "#1890ff" : "inherit" }}>
                          {unit.displayName}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
                          {unit.address}
                        </div>
                        <div style={{ fontSize: "13px" }}>
                          <span style={{ color: "#666" }}>{unit.count} invoices</span>
                          {" • "}
                          <span style={{ fontWeight: "500" }}>
                            {this.formatCurrency(unit.totalAmount, unit.currency)}
                          </span>
                        </div>
                      </div>
                      <Button
                        type="default"
                        size="small"
                        icon={<DownloadOutlined />}
                        disabled
                        // onClick={(e) => {
                        //   e.stopPropagation(); // Prevent card click when clicking download
                        //   this.handleDownloadByBillingUnit(unit);
                        // }}
                        // loading={downloadingBillingUnit === unit.billingUnit}
                        // disabled={downloadingAll || (downloadingBillingUnit !== null && downloadingBillingUnit !== unit.billingUnit)}
                        title={t.CONDO_INVOICE_DOWNLOAD || "Download"}
                      />
                    </div>
                  </div>
                );
              })}
              </div>
            )}
          </div>
        )}

        {dataSource.length === 0 && !condoInvoicesLoading ? (
          <NoContent
            message={t.CONDO_INVOICES_NO_DATA}
            image={ContractsImage}
          />
        ) : (
          <Table
            key={`${selectedBillingUnit || 'all'}-${searchTerm || 'none'}`} // Force re-render when any filter changes
            columns={columns}
            dataSource={dataSource}
            pagination={pagination}
            rowKey={(record) => `${record.documentId}-${record.contractNumber}-${record.prepaidNumber || 'none'}-${record.invoiceId}`}
            scroll={{ x: true }}
            loading={condoInvoicesLoading}
            style={{ marginBottom: "30px" }}
          />
        )}

        <style>{`
          .sidebar-aware-modal {
            padding-left: 200px !important;
          }
          .sidebar-aware-modal .ant-modal {
            max-width: calc(100vw - 240px);
          }
        `}</style>
        <Modal
          title={t.CONDO_INVOICE_PREVIEW}
          open={previewModalVisible}
          onCancel={this.handleClosePreview}
          wrapClassName="sidebar-aware-modal"
          width="90%"
          style={{ top: 20 }}
          footer={[
            <Button key="close" onClick={this.handleClosePreview}>
              {t.CONDO_INVOICE_CLOSE}
            </Button>
          ]}
        >
          {previewLoading ? (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <Spin size="large" />
              <p style={{ marginTop: "20px" }}>{t.CONDO_INVOICE_PDF_LOADING}</p>
            </div>
          ) : previewError ? (
            <Alert
              message={previewError}
              type="error"
              style={{ margin: "20px 0" }}
            />
          ) : previewPdfUrl ? (
            <iframe
              src={previewPdfUrl}
              style={{
                width: "100%",
                height: "80vh",
                border: "none",
              }}
              title="Invoice PDF Preview"
            />
          ) : null}
        </Modal>
      </div>
    );
  }
}

export default inject("contractStore")(
  withRouter(observer(CondoInvoicesList))
);

